import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  MapPin,
  Calendar,
  Search,
  Loader2,
  AlertCircle,
  ChevronRight,
  Clock,
  Check,
  X,
  FileText,
  Mail,
  Eye,
} from 'lucide-react';
import axios from 'axios';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ENTITY_TYPE_LABELS = {
  us_llc: 'LLC',
  us_c_corp: 'C-Corp',
  us_s_corp: 'S-Corp',
  us_nonprofit: 'Nonprofit',
  us_partnership: 'Partnership',
  us_sole_prop: 'Sole Proprietorship',
};

const DOC_TYPE_LABELS = {
  operating_agreement: 'Operating Agreement',
  articles_of_organization: 'Formation Certificate',
  ein_letter: 'EIN Letter',
  corporate_bylaws: 'Corporate Bylaws',
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Modal that opens for a pending-review company: documents + Approve/Reject
const ReviewModal = ({ company, onClose, onActionComplete }) => {
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [ownerEmail, setOwnerEmail] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const token = localStorage.getItem('auth_token');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setDocsLoading(true);
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [docsRes, ownerRes] = await Promise.all([
          axios.get(`${API}/companies/${company.id}/documents`, { headers }),
          axios
            .get(`${API}/admin/users/${company.user_id}`, { headers })
            .catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setDocuments(docsRes.data?.documents || []);
        setOwnerEmail(ownerRes.data?.email || null);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Failed to load documents');
      } finally {
        if (!cancelled) setDocsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [company.id, company.user_id, token]);

  const handleApprove = async () => {
    setSubmitting(true);
    setError('');
    try {
      await axios.post(
        `${API}/admin/companies/${company.id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onActionComplete();
    } catch (err) {
      setError(err.response?.data?.detail || 'Approve failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    const reason = rejectReason.trim();
    if (!reason) {
      setError('Rejection reason is required');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(
        `${API}/admin/companies/${company.id}/reject`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onActionComplete();
    } catch (err) {
      setError(err.response?.data?.detail || 'Reject failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="review-modal-backdrop"
    >
      <div
        className="bg-[#0D1117] border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="review-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-white" data-testid="review-modal-title">
              Review: {company.company_name}
            </h2>
            <p className="text-gray-400 text-sm mt-1">
              {ENTITY_TYPE_LABELS[company.entity_type] || company.entity_type} ·{' '}
              {company.state_of_formation} · Submitted {formatDate(company.submitted_at)}
            </p>
            {ownerEmail && (
              <p className="text-gray-500 text-xs mt-1 flex items-center gap-1">
                <Mail className="w-3 h-3" />
                {ownerEmail}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
            data-testid="review-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Documents list */}
        <div className="p-6">
          <h3 className="text-sm font-medium text-gray-300 uppercase tracking-wider mb-3">
            Submitted Documents
          </h3>
          {docsLoading ? (
            <div className="flex items-center gap-2 text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading documents…
            </div>
          ) : documents.length === 0 ? (
            <p className="text-gray-500 text-sm">No documents uploaded.</p>
          ) : (
            <div className="space-y-2" data-testid="review-modal-documents">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between bg-gray-800/40 border border-gray-700 rounded-lg p-3"
                  data-testid={`review-doc-${doc.document_type}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-5 h-5 text-[#00E87A] flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white text-sm font-medium">
                        {DOC_TYPE_LABELS[doc.document_type] || doc.document_type}
                      </p>
                      <p
                        className="text-gray-500 text-xs truncate max-w-[300px]"
                        title={doc.file_name}
                      >
                        {doc.file_name}
                      </p>
                    </div>
                  </div>
                  <a
                    href={doc.presigned_url || doc.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-gray-300 border border-gray-600 rounded hover:bg-gray-700"
                    data-testid={`review-doc-view-${doc.document_type}`}
                  >
                    <Eye className="w-3.5 h-3.5" /> View
                  </a>
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-300">
              {error}
            </div>
          )}

          {/* Reject inline reason input */}
          {showRejectInput && (
            <div className="mt-5 space-y-2" data-testid="reject-reason-block">
              <label className="text-xs text-gray-400 block">Rejection reason (visible to client)</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g. Operating Agreement is not signed by all members"
                rows={3}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-red-500/60 resize-none"
                data-testid="reject-reason-input"
                autoFocus
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 p-6 border-t border-gray-700">
          {showRejectInput ? (
            <>
              <button
                onClick={() => {
                  setShowRejectInput(false);
                  setRejectReason('');
                  setError('');
                }}
                disabled={submitting}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white"
                data-testid="reject-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={submitting || !rejectReason.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-300 hover:bg-red-500/30 text-sm font-medium rounded-lg disabled:opacity-50"
                data-testid="reject-confirm-btn"
              >
                <X className="w-4 h-4" />
                {submitting ? 'Rejecting…' : 'Confirm Reject'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setShowRejectInput(true)}
                disabled={submitting}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/15 text-red-300 hover:bg-red-500/25 text-sm font-medium rounded-lg disabled:opacity-50"
                data-testid="reject-btn"
              >
                <X className="w-4 h-4" />
                Reject
              </button>
              <button
                onClick={handleApprove}
                disabled={submitting}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#00E87A] text-gray-900 hover:opacity-90 text-sm font-medium rounded-lg disabled:opacity-50"
                data-testid="approve-btn"
              >
                <Check className="w-4 h-4" />
                {submitting ? 'Approving…' : 'Approve'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Pending review queue: list of self-registered companies awaiting admin action
const PendingReviewQueue = ({ companies, ownerEmails, onSelect }) => {
  if (companies.length === 0) {
    return (
      <div className="bg-gray-800/30 border border-gray-700/50 rounded-xl p-12 text-center">
        <Clock className="w-10 h-10 text-gray-600 mx-auto mb-3" />
        <p className="text-gray-400">No companies awaiting review</p>
      </div>
    );
  }
  return (
    <div className="bg-gray-800/30 border border-gray-700/50 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700/50">
              <th className="text-left px-6 py-4 text-xs font-medium text-gray-400 uppercase tracking-wider">
                Company
              </th>
              <th className="text-left px-6 py-4 text-xs font-medium text-gray-400 uppercase tracking-wider">
                Entity
              </th>
              <th className="text-left px-6 py-4 text-xs font-medium text-gray-400 uppercase tracking-wider">
                State
              </th>
              <th className="text-left px-6 py-4 text-xs font-medium text-gray-400 uppercase tracking-wider">
                Submitted
              </th>
              <th className="text-left px-6 py-4 text-xs font-medium text-gray-400 uppercase tracking-wider">
                Client
              </th>
              <th className="text-right px-6 py-4 text-xs font-medium text-gray-400 uppercase tracking-wider">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/50">
            {companies.map((c) => (
              <tr
                key={c.id}
                className="hover:bg-gray-800/30 transition-colors"
                data-testid={`pending-review-row-${c.id}`}
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-amber-400" />
                    </div>
                    <span className="text-white font-medium">{c.company_name}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="px-2.5 py-1 bg-gray-700/50 text-gray-300 text-sm rounded-md">
                    {ENTITY_TYPE_LABELS[c.entity_type] || c.entity_type || '-'}
                  </span>
                </td>
                <td className="px-6 py-4 text-gray-300">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-500" />
                    {c.state_of_formation || '-'}
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-400 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    {formatDate(c.submitted_at)}
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-300 text-sm">
                  {ownerEmails[c.user_id] || (
                    <span className="text-gray-500 italic">unknown</span>
                  )}
                </td>
                <td className="px-6 py-4 text-right">
                  <button
                    onClick={() => onSelect(c)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#00E87A] text-gray-900 text-sm font-medium rounded-lg hover:opacity-90"
                    data-testid={`pending-review-open-${c.id}`}
                  >
                    Review
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SuperAdminCompanies = () => {
  const [tab, setTab] = useState('all'); // 'all' | 'pending'
  const [companies, setCompanies] = useState([]);
  const [pending, setPending] = useState([]);
  const [pendingOwners, setPendingOwners] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [total, setTotal] = useState(0);
  const [reviewing, setReviewing] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };
      const [allRes, pendingRes] = await Promise.all([
        axios.get(`${API}/admin/companies`, { headers }),
        axios.get(`${API}/admin/companies?approval_status=pending_review`, { headers }),
      ]);
      setCompanies(allRes.data.companies || []);
      setTotal(allRes.data.total || 0);
      const pendingList = pendingRes.data.companies || [];
      setPending(pendingList);

      // Resolve owner emails for pending list (one batched lookup)
      const ownerIds = [...new Set(pendingList.map((c) => c.user_id).filter(Boolean))];
      if (ownerIds.length > 0) {
        const lookups = await Promise.all(
          ownerIds.map((uid) =>
            axios
              .get(`${API}/admin/users/${uid}`, { headers })
              .then((r) => [uid, r.data?.email])
              .catch(() => [uid, null])
          )
        );
        setPendingOwners(Object.fromEntries(lookups));
      } else {
        setPendingOwners({});
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching companies:', err);
      setError('Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const filteredCompanies = companies.filter(
    (company) =>
      company.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.state_of_formation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.entity_type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleReviewDone = () => {
    setReviewing(null);
    fetchAll();
  };

  return (
    <SuperAdminLayout>
      <div className="p-6" data-testid="super-admin-companies">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Companies</h1>
            <p className="text-gray-400 text-sm mt-1">
              {tab === 'all' ? `${total} total companies` : `${pending.length} awaiting review`}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 border-b border-gray-700/50" data-testid="companies-tabs">
          <button
            onClick={() => setTab('all')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === 'all'
                ? 'border-[#00E87A] text-[#00E87A]'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
            data-testid="tab-all-companies"
          >
            All Companies
          </button>
          <button
            onClick={() => setTab('pending')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              tab === 'pending'
                ? 'border-amber-400 text-amber-400'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
            data-testid="tab-pending-review"
          >
            <Clock className="w-4 h-4" />
            Pending Review
            {pending.length > 0 && (
              <span className="px-1.5 py-0.5 text-xs bg-amber-500/20 text-amber-300 rounded">
                {pending.length}
              </span>
            )}
          </button>
        </div>

        {/* Search (All tab only) */}
        {tab === 'all' && (
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder="Search by name, state, or entity type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#00E87A] transition-colors"
                data-testid="companies-search"
              />
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#00E87A] animate-spin" />
          </div>
        ) : tab === 'pending' ? (
          <PendingReviewQueue
            companies={pending}
            ownerEmails={pendingOwners}
            onSelect={setReviewing}
          />
        ) : (
          /* All Companies Table */
          <div className="bg-gray-800/30 border border-gray-700/50 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700/50">
                    <th className="text-left px-6 py-4 text-xs font-medium text-gray-400 uppercase tracking-wider">Company</th>
                    <th className="text-left px-6 py-4 text-xs font-medium text-gray-400 uppercase tracking-wider">State</th>
                    <th className="text-left px-6 py-4 text-xs font-medium text-gray-400 uppercase tracking-wider">Entity Type</th>
                    <th className="text-left px-6 py-4 text-xs font-medium text-gray-400 uppercase tracking-wider">Created</th>
                    <th className="text-right px-6 py-4 text-xs font-medium text-gray-400 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/50">
                  {filteredCompanies.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                        {searchTerm ? 'No companies match your search' : 'No companies found'}
                      </td>
                    </tr>
                  ) : (
                    filteredCompanies.map((company) => (
                      <tr
                        key={company.id}
                        className="hover:bg-gray-800/30 transition-colors"
                        data-testid={`company-row-${company.id}`}
                      >
                        <td className="px-6 py-4">
                          <Link
                            to={`/superadmin/companies/${company.id}`}
                            className="flex items-center gap-3 group"
                          >
                            <div className="w-10 h-10 rounded-lg bg-[#00E87A]/10 flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-[#00E87A]" />
                            </div>
                            <span className="text-white font-medium group-hover:text-[#00E87A] transition-colors">
                              {company.company_name}
                            </span>
                          </Link>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 text-gray-300">
                            <MapPin className="w-4 h-4 text-gray-500" />
                            {company.state_of_formation || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-2.5 py-1 bg-gray-700/50 text-gray-300 text-sm rounded-md">
                            {ENTITY_TYPE_LABELS[company.entity_type] || company.entity_type || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 text-gray-400 text-sm">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            {formatDate(company.created_at)}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <Link
                            to={`/superadmin/companies/${company.id}`}
                            className="inline-flex items-center gap-1 text-[#00E87A] hover:text-[#00E87A]/80 text-sm font-medium transition-colors"
                            data-testid={`view-company-${company.id}`}
                          >
                            View
                            <ChevronRight className="w-4 h-4" />
                          </Link>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {reviewing && (
        <ReviewModal
          company={reviewing}
          onClose={() => setReviewing(null)}
          onActionComplete={handleReviewDone}
        />
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminCompanies;
